import watchlists from '../slices/watchlists';
import type { RootStateType, ThunkDispatchType } from '../index';

import {
    getWatchlists,
    setWatchlistActive,
    updateWatchlist
} from '../../api/watchlists';
import { filterMarketEntities, watchlistToMarketFilters } from '../../utils/filter-market-entities';
import { pickLastByKey } from '../../utils';
import { forgetWatchlistInMarket } from './market';

export function fetchAllWatchlists() {
    return async (dispatch: ThunkDispatchType, getState: () => RootStateType) => {
        try {
            dispatch(watchlists.actions.fetchAll());
            const data = (await getWatchlists()).map((w: any) => {
                const watchlistEntities = filterMarketEntities({
                    entities: [...getState().market.allOpenJobs, ...getState().market.allOpenCapacities],
                    filters: watchlistToMarketFilters(w)
                });

                return ({
                    ...w,
                    publishedCount: watchlistEntities.length,
                    lastPublishedDate: pickLastByKey(watchlistEntities, 'publishedAt')
                });
            });
            dispatch(watchlists.actions.allFetched({ data }));
            // A watchlist can be gone, e.g. when the company left its group or it was deleted on another device
            const { watchlistId, sourceWatchlistId } = getState().market.filters;
            [watchlistId, sourceWatchlistId]
                .filter((id) => id && !data.some((w: any) => String(w.id) === String(id)))
                .forEach((id) => dispatch(forgetWatchlistInMarket(id as number | string)));
        } catch (e) {
            console.log('ERROR fetching watchlists, set redux error state and handle in errorBoundary.', e);
            dispatch(watchlists.actions.allFetched({ data: [] }));
        }
    };
}

/** Throws the API error for the caller; the list is updated once the API accepted the change. */
export function toggleWatchlistActive(id: number | string, isActive: boolean) {
    return async (dispatch: ThunkDispatchType) => {
        const updated = await setWatchlistActive(id, isActive);
        dispatch(watchlists.actions.activeChanged({ id, isActive: updated.isActive }));
    };
}

/** Switches a watchlist on with one channel; sends only the three flags. Throws the API error for the caller. */
export function setWatchlistNotificationMode(id: number | string, mode: 'instant' | 'daily') {
    return async (dispatch: ThunkDispatchType) => {
        const updated = await updateWatchlist(id, {
            isActive: true,
            instantNotifications: mode === 'instant',
            dailyNotifications: mode === 'daily'
        });
        dispatch(watchlists.actions.notificationModeChanged({
            id,
            isActive: updated.isActive,
            instantNotifications: updated.instantNotifications,
            dailyNotifications: updated.dailyNotifications
        }));
    };
}

export const selectAllWatchlists = () => (state: RootStateType) => state.watchlists.all;
export const selectFetchingAllWatchlists = () => (state: RootStateType) => state.watchlists.fetchingAll;
